import { db } from "../firebaseClient"
import { doc, setDoc, deleteDoc, getDocs, collection, query, orderBy } from "firebase/firestore"

// Syncs a course into the public catalog when it's published + public.
// Uses provider-agnostic field names (providerType/providerId/providerName) so this
// same collection can later hold educator-authored courses without any schema change —
// the catalog UI only ever needs to read providerName/providerVerified, never "org" directly.
export const syncCourseToCatalog = async ({ orgId, orgName, orgVerified, orgNesolearnId, course }) => {
  try {
    const listingRef = doc(db, "catalogListings", course.courseId)
    await setDoc(listingRef, {
      courseId: course.courseId,
      title: course.title,
      titleLower: course.titleLower || course.title?.toLowerCase() || "",
      description: course.description || "",
      thumbnailUrl: course.thumbnailUrl || "",
      category: course.category || "Other",
      courseType: course.courseType || "course",
      enrollmentType: course.enrollmentType || "invite",
      priceNesol: course?.pricing?.isFree ? 0 : course?.pricing?.amount || 0,
      isFree: course?.pricing?.isFree !== false,
      moduleCount: course?.modules?.length || 0,
      lessonCount:
        course?.modules?.reduce((sum, m) => sum + (m?.lessons?.length || 0), 0) || 0,
      providerType: "organization",
      providerId: orgId,
      providerName: orgName,
      providerVerified: !!orgVerified,
      providerNesolearnId: orgNesolearnId ? `NESO-${orgNesolearnId}` : "",
      publishedAt: new Date().toISOString(),
    })
    return { error: null }
  } catch (error) {
    return { error }
  }
}

// Removes a course from the public catalog (unpublished or set back to private)
export const removeCourseFromCatalog = async (courseId) => {
  try {
    const listingRef = doc(db, "catalogListings", courseId)
    await deleteDoc(listingRef)
    return { error: null }
  } catch (error) {
    return { error }
  }
}

// Syncs a standalone learning path into the SAME public catalog collection
// courses use — catalogListings was deliberately built provider-agnostic for
// exactly this. Denormalizes node course IDs directly onto the listing
// (mirroring how course listings store moduleCount/lessonCount instead of
// full modules) so browsing/enrolling never needs a per-org path fetch.
// listingType distinguishes this from a course listing. Existing course
// listings have no listingType field — code reading this collection should
// treat a MISSING listingType as "course", never require the field.
export const syncPathToCatalog = async ({ orgId, orgName, orgVerified, orgNesolearnId, path }) => {
  try {
    const listingRef = doc(db, "catalogListings", path.pathId)
    await setDoc(listingRef, {
      listingType: "path",
      pathId: path.pathId,
      title: path.title,
      titleLower: path.title?.toLowerCase() || "",
      description: path.description || "",
      nodes: path.nodes || [],
      nodeCount: path?.nodes?.length || 0,
      providerType: "organization",
      providerId: orgId,
      providerName: orgName,
      providerVerified: !!orgVerified,
      providerNesolearnId: orgNesolearnId ? `NESO-${orgNesolearnId}` : "",
      publishedAt: new Date().toISOString(),
    })
    return { error: null }
  } catch (error) {
    return { error }
  }
}

export const removePathFromCatalog = async (pathId) => {
  try {
    await deleteDoc(doc(db, "catalogListings", pathId))
    return { error: null }
  } catch (error) {
    return { error }
  }
}

// Lists all public catalog listings, A-Z by title
export const listCatalogListings = async () => {
  try {
    const listingsRef = collection(db, "catalogListings")
    const q = query(listingsRef, orderBy("titleLower", "asc"))
    const snap = await getDocs(q)
    const listings = snap.docs?.map((d) => d.data())
    return { listings, error: null }
  } catch (error) {
    // Fallback without ordering, in case the index isn't built yet
    try {
      const listingsRef = collection(db, "catalogListings")
      const snap = await getDocs(listingsRef)
      const listings = snap.docs?.map((d) => d.data())
      listings.sort((a, b) => (a?.titleLower || "").localeCompare(b?.titleLower || ""))
      return { listings, error: null }
    } catch (fallbackError) {
      return { listings: [], error: fallbackError }
    }
  }
}